import { useState } from 'react'
import type { KeyboardEvent, FocusEvent } from 'react'
import { classNames } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorRecipientCommitResult } from '@/msgdeskRuntime/contracts/editorActions'
import { useEditorSession } from '../../EditorSessionContext'
import { useEditorSurface } from '../../EditorSurfaceContext'

// Recipient smart input — interaction is component-owned; resolution, insertion,
// model synchronization, and token events use the shared editor action pipeline.

type RecipientError = NonNullable<EditorRecipientCommitResult['reason']>

// Legacy parity: the checker refused invalid input silently-but-visibly
// (validator error state, value kept). The React input mirrors that with an
// inline error look plus a short hint; typing clears it.
const ERROR_HINTS: Record<RecipientError, string> = {
  invalid: 'Enter an email or @name',
  duplicate: 'Already added',
  'not-found': 'Msg not found',
}

export function RecipientSmartInput() {
  const { skin } = useEditorSurface()
  const editorSession = useEditorSession()
  const isUpdate = editorSession.mode === 'update_msg'
  const [error, setError] = useState<RecipientError | null>(null)

  // BEHAVIOUR: commit a typed recipient. "@name" → msg recipient (legacy
  // resolves the key); a valid email → email recipient; anything else is
  // refused with the legacy validator rule (never becomes a token). On
  // success we mirror a token and clear the input; on refusal the value
  // STAYS for correction (legacy behavior) and the error look appears.
  const commitRecipient = (input: HTMLInputElement) => {
    if (isUpdate || input.disabled) return
    const value = input.value.trim()
    if (!value) return

    const shouldRestoreFocus = value.startsWith('@')
    if (shouldRestoreFocus) input.disabled = true

    void dispatchEditorAction({ action: 'commit-recipient', value }).then(result => {
      input.disabled = isUpdate
      if (result.committed) input.value = ''
      setError(result.committed ? null : result.reason ?? null)
      if (shouldRestoreFocus) input.focus()
    })
  }

  // BEHAVIOUR: legacy checker is bound to focusout; keep a named React hook too
  // so the input stays smart even if legacy rebinding is delayed.
  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    commitRecipient(event.currentTarget)
  }

  // BEHAVIOUR: Enter commits email/@msg recipients.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    commitRecipient(event.currentTarget)
  }

  return (
    <div className={classNames('md-to__input-wrap', skin.recipients.inputWrap)}>
      <input
        className={classNames(
          skin.recipients.input,
          error && 'md-to__input--err',
          error && skin.recipients.inputErr,
        )}
        name="to_address"
        placeholder={isUpdate ? 'Recipients cannot be changed after sending' : 'Recipients'}
        type="text"
        disabled={isUpdate}
        autoComplete="off"
        title={error ? ERROR_HINTS[error] : undefined}
        onInput={event => {
          if (error) setError(null)
          dispatchEditorAction({ action: 'sync-input', target: event.currentTarget, reason: 'react-recipients:input' })
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {error ? (
        <span className={classNames('md-to__input-err-hint', skin.recipients.inputErrHint)}>
          {ERROR_HINTS[error]}
        </span>
      ) : null}
      <input name="email_to" type="hidden" defaultValue="" />
      <input name="email_from" type="hidden" defaultValue="" />
    </div>
  )
}
